import { useState } from 'react'
import { Link, NavLink, useSearchParams } from 'react-router-dom'
import { searchPathForTagName } from '@/api/taxonomy'
import {
  BrandIcon,
  MenuIcon,
  PlusIcon,
  RankingIcon,
  SearchIcon,
  TagIcon,
  TutorialIcon,
} from '@/components/icons'
import { SearchInput } from '@/components/SearchInput'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button, ButtonLink, Drawer } from '@/components/ui'
import { useAuth } from '@/stores/AuthProvider'
import { cn } from '@/utils/cn'
import { SideNav } from './SideNav'
import { UserMenu } from './UserMenu'

/**
 * 顶栏（设计稿 D1/D2/D3 顶部，M1/M2 顶部）。
 *
 * 桌面端与移动端是**两套结构**，不是同一套的缩放（AGENTS.md 4.3）：
 *
 *   桌面：Logo ┃ 排行 教程 ┃ ————搜索框———— ┃ +发布 主题 头像
 *   移动：☰ Logo ┃ ————————— ┃ 搜索图标 头像      ← 搜索框折叠成图标
 *
 * 移动端把搜索框折叠成图标是设计稿 M1 的做法：窄屏里一个长输入框会把 Logo 和
 * 头像挤到没有余地。点图标后就地展开成整行输入框（而不是跳到搜索页），
 * 这样用户不会丢失当前浏览位置。
 */
export function AppHeader() {
  const [navOpen, setNavOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [searchParams] = useSearchParams()
  const { status } = useAuth()

  const currentQuery = searchParams.get('q') ?? ''

  return (
    <>
      {/*
        sticky 而不是 fixed：fixed 需要给 body 补 padding-top，一旦顶栏高度在
        断点间变化就会对不齐。sticky 由文档流自己算高度。
      */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex h-14 max-w-[100rem] items-center gap-2 px-3 sm:h-16 sm:gap-3 sm:px-4 lg:px-6">
          {/* 移动端汉堡菜单：桌面端有固定侧栏，不需要它。 */}
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="打开导航菜单"
            className="-ml-1 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-surface-muted hover:text-foreground lg:hidden"
          >
            <MenuIcon className="size-5" />
          </button>

          {mobileSearchOpen ? (
            // 移动端展开态：整行让给搜索框，其余控件暂时隐去。
            <div className="flex flex-1 items-center gap-2 sm:hidden">
              <SearchInput autoFocus />
              <Button variant="ghost" size="sm" onClick={() => setMobileSearchOpen(false)}>
                取消
              </Button>
            </div>
          ) : (
            <>
              <Link
                to="/"
                className="flex shrink-0 items-center gap-2 rounded-lg py-1 pr-1 text-foreground"
                aria-label="APKGal 首页"
              >
                <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground sm:size-9">
                  <BrandIcon className="size-5" />
                </span>
                <span className="text-base font-bold tracking-tight sm:text-lg">APKGal</span>
              </Link>

              {/* 桌面端的两个快捷入口，移动端收进抽屉。 */}
              <nav aria-label="快捷入口" className="ml-2 hidden items-center gap-1 lg:flex">
                <HeaderNavLink to="/ranking" icon={<RankingIcon className="size-4" />}>
                  排行
                </HeaderNavLink>
                {/*
                  「标签大全」是一个真实路由（/tags），不像下面的「教程」那样靠
                  按名检索凑——它自己就是一个页面，不需要任何标签的 public_id。
                */}
                <HeaderNavLink to="/tags" icon={<TagIcon className="size-4" />}>
                  标签大全
                </HeaderNavLink>
                {/*
                  「教程」是一个后端标签，但顶栏是静态的、拿不到它的 public_id
                  （`GET /tags` 是异步的，为一个入口等一次请求不值得）。所以这里用
                  按名条件 `?tag=教程` —— 它是后端四种条件类型之一，不是兜底。
                  代价是同名标签会跨栏一起命中，对一个浏览入口可以接受。
                */}
                <HeaderNavLink
                  to={searchPathForTagName('教程')}
                  icon={<TutorialIcon className="size-4" />}
                >
                  教程
                </HeaderNavLink>
              </nav>

              {/* 桌面端常驻搜索框 */}
              <div className="ml-2 hidden flex-1 sm:flex">
                <SearchInput value={currentQuery} />
              </div>

              <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setMobileSearchOpen(true)}
                  aria-label="搜索"
                  className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-surface-muted hover:text-foreground sm:hidden"
                >
                  <SearchIcon className="size-5" />
                </button>

                {/*
                  发布入口：游客看不到（后端的发布相关路由全在 /admin 之下，
                  见 docs/backend-sync/2026-08-07-publish-flow-diff.md），
                  未登录点进去只会拿到 401，不如不显示。
                */}
                {status === 'authenticated' && (
                  <ButtonLink
                    to="/publish/resource"
                    size="md"
                    className="hidden sm:inline-flex"
                    leadingIcon={<PlusIcon className="size-4" />}
                  >
                    发布
                  </ButtonLink>
                )}

                <ThemeToggle />
                <UserMenu />
              </div>
            </>
          )}
        </div>
      </header>

      {/* 移动端导航抽屉（设计稿 M2） */}
      <Drawer
        open={navOpen}
        onOpenChange={setNavOpen}
        title="APKGal 导航"
        header={
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BrandIcon className="size-5" />
            </span>
            <span className="text-base font-bold text-foreground">APKGal</span>
          </div>
        }
      >
        <MobileNavBody onNavigate={() => setNavOpen(false)} />
      </Drawer>
    </>
  )
}

function HeaderNavLink({
  to,
  icon,
  children,
}: {
  to: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary-soft text-primary'
            : 'text-muted-foreground hover:bg-surface-muted hover:text-foreground',
        )
      }
    >
      <span aria-hidden="true">{icon}</span>
      {children}
    </NavLink>
  )
}

/** 抽屉内容（设计稿 M2）：账号区 + 分类树 + 发布按钮。 */
function MobileNavBody({ onNavigate }: { onNavigate: () => void }) {
  const { status, user } = useAuth()

  return (
    <div className="flex min-h-full flex-col">
      {/* 账号区：设计稿 M2 顶部那块「未登录 / 登录 · 注册」 */}
      <div className="border-b border-border p-4">
        {status === 'authenticated' ? (
          <Link to="/favorites" onClick={onNavigate} className="flex items-center gap-3">
            <span className="size-10 shrink-0 rounded-lg bg-surface-muted" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">
                {user?.display_name}
              </span>
              <span className="block text-xs text-primary">查看我的收藏</span>
            </span>
          </Link>
        ) : (
          <div className="flex items-center gap-3">
            <span className="size-10 shrink-0 rounded-lg bg-surface-muted" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">未登录</span>
              <span className="flex items-center gap-1 text-xs">
                <Link to="/login" onClick={onNavigate} className="text-primary hover:underline">
                  登录
                </Link>
                <span className="text-subtle-foreground">·</span>
                <Link to="/register" onClick={onNavigate} className="text-primary hover:underline">
                  注册
                </Link>
              </span>
            </span>
          </div>
        )}
      </div>

      {/* 移动端把桌面顶栏的快捷入口也收进来，否则窄屏无从到达 */}
      <nav aria-label="快捷入口" className="border-b border-border p-4">
        <ul className="space-y-0.5">
          <li>
            <MobileNavItem to="/ranking" onNavigate={onNavigate}>
              <RankingIcon className="size-4 text-muted-foreground" />
              排行
            </MobileNavItem>
          </li>
          <li>
            <MobileNavItem to="/tags" onNavigate={onNavigate}>
              <TagIcon className="size-4 text-muted-foreground" />
              标签大全
            </MobileNavItem>
          </li>
          <li>
            <MobileNavItem to="/search" onNavigate={onNavigate}>
              <SearchIcon className="size-4 text-muted-foreground" />
              浏览全部
            </MobileNavItem>
          </li>
        </ul>
      </nav>

      <SideNav variant="bare" onNavigate={onNavigate} />

      {/*
        两个发布入口，不是一个。

        站点有两种可发布的内容（整合包与教程，设计稿 D5 / D6），此前抽屉里只有
        一个笼统的「发布内容」指向整合包 —— 教程页因此在移动端完全不可达。
        并排给出比先进一个页面再切类型少一次点击，抽屉本来就是「去哪儿」的地方。
      */}
      <div className="mt-auto space-y-2 border-t border-border p-4">
        {status === 'authenticated' ? (
          <>
            <ButtonLink
              to="/publish/resource"
              block
              onClick={onNavigate}
              leadingIcon={<PlusIcon className="size-4" />}
            >
              发布移植包
            </ButtonLink>
            <ButtonLink
              to="/publish/tutorial"
              block
              variant="secondary"
              onClick={onNavigate}
              leadingIcon={<TutorialIcon className="size-4" />}
            >
              发布教程
            </ButtonLink>
          </>
        ) : (
          <ButtonLink to="/login" block onClick={onNavigate}>
            登录后发布内容
          </ButtonLink>
        )}
      </div>
    </div>
  )
}

function MobileNavItem({
  to,
  onNavigate,
  children,
}: {
  to: string
  onNavigate: () => void
  children: React.ReactNode
}) {
  // 与 SideNav 的标签条目视觉保持一致，避免抽屉里出现两种列表样式。
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      end
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-primary-soft text-primary' : 'text-foreground hover:bg-surface-muted',
        )
      }
    >
      {children}
    </NavLink>
  )
}
